import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Button,
    TextField, Container, Box, Modal, Tooltip,
    Paper,
    Typography,
    AppBar,
    Toolbar,
    MenuItem,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditRoutes = () => {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const { routeID } = useParams();
    const navigate = useNavigate();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [routeName, setRouteName] = useState("");
    const [distance, setDistance] = useState("");
    const [routeCharge, setRouteCharge] = useState("");
    const [description, setDescription] = useState("");

    const [loadingBarProgress, setLoadingBarProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ Fetch branch list
    const fetchBranches = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const res = await fetch(`${apiUrl}/Branch/GetBranch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                },
                body: JSON.stringify({}),
            });
            const data = await res.json();
            setBranches(data);
        } catch (err) {
            console.error("Failed to fetch branches:", err);
        }
    };

    // ✅ Fetch route details
    const fetchRouteDetails = async (searchTerm = "", pageNumber = 1, pageSize = 10) => {
        try {
            setLoadingBarProgress(30);
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const res = await fetch(`${apiUrl}/Transportation/Route/GetRoutes`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                },
                body: JSON.stringify({
                    searchTerm,
                    pageNumber,
                    pageSize,
                }),
            });

            if (!res.ok) throw new Error("Failed to fetch route");

            const data = await res.json();
            const routes = data.routes;
            const routeData = routes.find(
                (route) => route.routeID === parseInt(routeID)
            );

            if (routeData) {
                setBranchId(routeData.branchId || "");
                setRouteName(routeData.routeName || "");
                setDistance(routeData.distance || "");
                setRouteCharge(routeData.routeCharge || "");
                setDescription(routeData.description || "")
            } else {
                setError("Route not found");
            }
        } catch (err) {
            console.error("Error:", err);
            setError("An error occurred while fetching route data");
        } finally {
            setIsLoading(false);
            setLoadingBarProgress(100);
        }
    };

    // ✅ Handle update submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const branch = parseInt(branchId);
        const dist = parseFloat(distance);
        const charge = parseFloat(routeCharge);

        if (!routeName || isNaN(branch) || isNaN(dist) || isNaN(charge) || !description) {
            // alert("Please fill in all fields with valid data.");
            setSnackbarMessage('Please fill in all fields with valid data.');
            setOpenSnackbar(true);
            setSnackbarSeverity("warning");
            return;
        }

        const bodyData = {
            routeName: routeName.trim(),
            branchId: branch,
            distance: dist,
            routeCharge: charge,
            description: description.trim()
        };
        try {
            setLoadingBarProgress(30);
            const apiUrl = process.env.REACT_APP_BASE_URL;

            const res = await fetch(
                `${apiUrl}/Transportation/Route/${routeID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: sessionStorage.getItem("token"),
                    },
                    body: JSON.stringify(bodyData),
                }
            );

            if (res.ok) {
                setShowSuccessModal(true);

            } else {
                // alert("Failed to update route");
                setSnackbarMessage('Failed to update route');
                setOpenSnackbar(true);
                setSnackbarSeverity("error");
            }
        } catch (err) {
            console.error("Error updating route:", err);
            // alert("An error occurred while updating the route");
            setSnackbarMessage('An error occurred while updating the route');
            setOpenSnackbar(true);
            setSnackbarSeverity("error");
        } finally {
            setLoadingBarProgress(100);
        }
    };

    useEffect(() => {
        fetchBranches();
        fetchRouteDetails();
    }, [routeID]);
    useEffect(() => {
        if (!branchId && branches.length === 1 && !error) {
            // Only set if not already filled by route data
            setBranchId(String(branches[0].branchId));

        }
    }, [branches, branchId, error]);


    return (
        <Container >
            <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />
            <Box
                sx={{
                    backgroundColor: '#0B1F40', // dark navy blue
                    padding: '12px 24px',
                    boxShadow: 3,
                    borderRadius: '4px 4px 0 0',
                    margin: '2px auto',
                    maxWidth: 700,
                }}
            >
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                    Edit Routes
                </Typography> </Box>

            <Paper elevation={3} sx={{
                p: 4,
                maxWidth: 700,
                mx: "auto",
                mt: 1,
                borderRadius: 2,
                backgroundColor: "#ffffff",
            }}>
                {isLoading ? (
                    <Typography>Loading...</Typography>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <form onSubmit={handleSubmit}>

                        <TextField
                            select
                            label="Branch"
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            fullWidth
                            margin="normal"
                            required
                            disabled={branches.length <= 1}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.branchId} value={String(branch.branchId)}>
                                    {branch.branchName}
                                </MenuItem>

                            ))}
                        </TextField>

                        <TextField
                            label="Route Name"
                            value={routeName}
                            onChange={(e) => setRouteName(e.target.value)}
                            fullWidth
                            margin="normal"
                            required
                        />

                        <TextField
                            label="Distance (km)"
                            value={distance}
                            onChange={(e) => setDistance(e.target.value)}
                            fullWidth
                            margin="normal"
                            required
                        />

                        <TextField
                            label="Route Charge (₹)"
                            value={routeCharge}
                            onChange={(e) => setRouteCharge(e.target.value)}
                            fullWidth
                            margin="normal"
                            required
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            margin="normal"
                            multiline
                            rows={4}
                            variant="outlined"
                        />

                        <div style={{ marginTop: 16 }}>
                            <Tooltip title={(!routeName || !distance || !routeCharge) && "Please fill the required fields"} arrow><span>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    style={{ marginRight: 8 }}
                                    disabled={!routeName || !distance || !routeCharge}
                                >
                                    Save Changes
                                </Button> </span></Tooltip>
                            <Button variant="outlined" color="error" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}
            </Paper>


            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                aria-labelledby="success-modal-title"
                aria-describedby="success-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
                        <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
                            Route Updated ✅
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: '20px' }}>
                        <Typography id="success-modal-description" sx={{ mb: 2 }}>
                            The Route has been updated successfully.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    navigate(-1); // go back after closing modal
                                }}
                                variant="contained"
                                color="primary"
                            >
                                OK
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EditRoutes;
